import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

export const SIMULATION_STATUS = {
    finished: 'finished',
    idle: 'idle',
    paused: 'paused',
    running: 'running',
};

/**
 * Virtual clock of the verification simulation. Time only moves forward while
 * the clock runs, so pausing freezes signals, stage timers and the whole
 * scenario exactly where they are instead of hiding them.
 */
const useSimulationClock = ({
    duration,
    onAdvance,
}) => {
    const [status, setStatus] = useState(SIMULATION_STATUS.idle);
    const timeRef = useRef(0);
    const frameRef = useRef(null);
    const lastFrameRef = useRef(null);
    const advanceRef = useRef(onAdvance);

    useEffect(() => {
        advanceRef.current = onAdvance;
    }, [onAdvance]);

    useEffect(() => {
        if (status !== SIMULATION_STATUS.running) return undefined;

        const tick = (timestamp) => {
            const previousTimestamp = lastFrameRef.current === null
                ? timestamp
                : lastFrameRef.current;
            const fromTime = timeRef.current;
            const toTime = Math.min(duration, fromTime + (timestamp - previousTimestamp));

            lastFrameRef.current = timestamp;
            timeRef.current = toTime;
            advanceRef.current(fromTime, toTime);

            if (toTime >= duration) {
                setStatus(SIMULATION_STATUS.finished);

                return;
            }

            frameRef.current = requestAnimationFrame(tick);
        };

        frameRef.current = requestAnimationFrame(tick);

        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);

            frameRef.current = null;
            lastFrameRef.current = null;
        };
    }, [duration, status]);

    const getTime = useCallback(() => timeRef.current, []);

    const start = useCallback(() => {
        setStatus((currentStatus) => (
            currentStatus === SIMULATION_STATUS.finished
                ? currentStatus
                : SIMULATION_STATUS.running
        ));
    }, []);

    const pause = useCallback(() => {
        setStatus((currentStatus) => (
            currentStatus === SIMULATION_STATUS.running
                ? SIMULATION_STATUS.paused
                : currentStatus
        ));
    }, []);

    const reset = useCallback(() => {
        timeRef.current = 0;
        lastFrameRef.current = null;
        setStatus(SIMULATION_STATUS.idle);
    }, []);

    return {
        getTime,
        pause,
        reset,
        start,
        status,
    };
};

export default useSimulationClock;
